// Real IYAF content. All numbers, names, stories sourced from the official organisational
// profile (profile_FINAL_v2.md / page_content_FINAL.md / master_content.md).

import { legacyImages as L } from "@/data/legacyImages";

export const org = {
  name: "IYAF",
  fullName: "International Youth Ambassadors Foundation for Training and Community Development",
  founded: 2016,
  hq: "44 Queen Noor Street, Amman 11190, Jordan",
  email: "info@iyafglobal.com",
  phone: "+962 6 569 4177 Ext. 615",
  website: "iyafglobal.com",
  socials: {
    facebook: "https://facebook.com/IYAFME",
    instagram: "https://instagram.com/iyaf.global",
    linkedin: "https://linkedin.com/company/iyaf",
  },
  leadership: [
    { name: "Trad AlMasri", role: "Financial Lead & President" },
    { name: "Ameer Mubaslat", role: "Technical Lead & Program Director" },
  ],
  team: { fullTime: 2, partTime: 4, volunteers: 50, mentors: 113, consultingTrainers: 40 },
};

export const armColors = {
  hub: "#3663A8",
  social: "#FF9F1C",
  consult: "#8C8C8C",
  venture: "#E01E84",
  blue: "#3663A8",
  blueLight: "#567CB6",
};

export const arms = [
  {
    key: "hub",
    name: "IYAF Hub",
    tagline: "Spaces & Infrastructure",
    color: armColors.hub,
    href: "/iyaf-hub",
    status: "Active",
    short:
      "A 400 sqm collaborative space in Amman: training and meeting rooms, an event venue, co-working desks, a legal umbrella for early-stage initiatives, and a network of 113 expert mentors.",
    bullets: [
      "400 sqm space · 1 training room · 6 meeting rooms · 70-cap event hall",
      "Legal umbrella for initiative registration (15 to date)",
      "113 vetted expert mentors across disciplines",
      "432 events hosted · 5,000+ training hours delivered",
    ],
    metrics: [
      { k: "Initiatives hosted", v: "75" },
      { k: "Events hosted", v: "432" },
      { k: "Legal registrations", v: "15" },
      { k: "Mentors in network", v: "113" },
    ],
  },
  {
    key: "social",
    name: "IYAF Social",
    tagline: "Programs & Pathways",
    color: armColors.social,
    href: "/iyaf-social",
    status: "Active",
    short:
      "Two routes to economic independence: entrepreneurship and employment. Our 97/3 model focuses intensive support on the 3% with genuine potential, and redirects the rest to paths where they can thrive.",
    bullets: [
      "555 startups assessed using the 97/3 model",
      "1,000 entrepreneurs trained across 46 cohorts",
      "3,000 employment participants · 80% employed in 12 months",
      "8,000 employers · 500 VCs · 56 accelerators",
    ],
    metrics: [
      { k: "Startups assessed", v: "555" },
      { k: "Entrepreneurs trained", v: "1,000" },
      { k: "Employment participants", v: "3,000" },
      { k: "Funding secured", v: "$2M+" },
    ],
  },
  {
    key: "consult",
    name: "IYAF Consult",
    tagline: "Services & Sustainability",
    color: armColors.consult,
    href: "/iyaf-consult",
    status: "Active",
    short:
      "Professional services delivered exclusively to organisations with social impact mandates. 100% of profit is reinvested into youth programs. Launched 2024.",
    bullets: [
      "Innovation, strategy and design-thinking workshops",
      "Program design, M&E and impact measurement",
      "Research, evaluation and policy analysis",
      "Sustainability reporting (saves $10K–$50K vs. traditional firms)",
    ],
    metrics: [
      { k: "Engagements", v: "7" },
      { k: "Social impact hours", v: "4,000" },
      { k: "Client satisfaction", v: "100%" },
      { k: "Profit reinvested", v: "100%" },
    ],
  },
  {
    key: "venture",
    name: "IYAF Venture",
    tagline: "Acceleration · 2027",
    color: armColors.venture,
    href: "/iyaf-venture",
    status: "Launching 2027",
    short:
      "The fourth arm, closing the entrepreneurship pathway with structured incubation, acceleration, demo days and investor connections for the 3% who clear validation.",
    bullets: [
      "6–12 month incubation programs",
      "3–6 month acceleration tracks",
      "Investor pitch events & demo days",
      "Initial cohort: 20 high-potential startups",
    ],
    metrics: [
      { k: "Launch year", v: "2027" },
      { k: "First cohort", v: "20" },
      { k: "Incubation", v: "6–12 mo" },
      { k: "Acceleration", v: "3–6 mo" },
    ],
  },
];

export const heroStats = [
  { key: "youthSupported", label: "Youth supported", value: "4,500+", note: "across 5 countries" },
  { key: "startupsAssessed", label: "Startups assessed", value: "555", note: "with the 97/3 model" },
  { key: "employmentRate", label: "Employment rate", value: "80%", note: "within 12 months" },
  { key: "fundingSecured", label: "Funding secured", value: "$2M+", note: "by participants" },
];

export const wholeImpactMetrics = [
  { k: "Countries", v: "5" },
  { k: "Communities", v: "13" },
  { k: "Youth supported", v: "4,500+" },
  { k: "Startups assessed", v: "555" },
  { k: "Entrepreneurs trained", v: "1,000" },
  { k: "Program cohorts", v: "46" },
  { k: "Employment participants", v: "3,000" },
  { k: "Employment rate (12 mo.)", v: "80%" },
  { k: "Funding secured", v: "$2M+" },
  { k: "Events hosted", v: "432" },
  { k: "Training hours", v: "5,000+" },
  { k: "Expert mentors", v: "113" },
  { k: "Initiatives hosted", v: "75" },
  { k: "Legal registrations", v: "15" },
  { k: "Employers in database", v: "8,000" },
  { k: "Industries reached", v: "12" },
  { k: "VCs in database", v: "500" },
  { k: "Accelerators connected", v: "56" },
  { k: "Self-sustainability", v: "70%" },
  { k: "Profit reinvested", v: "100%" },
];

export const countries = [
  { name: "Jordan", role: "HQ · West Asia", coord: [31.95, 35.93], featured: true },
  { name: "Lebanon", role: "West Asia", coord: [33.85, 35.86] },
  { name: "Egypt", role: "North Africa", coord: [30.04, 31.24] },
  { name: "Uganda", role: "East Africa", coord: [0.35, 32.58] },
  { name: "Nepal", role: "South Asia", coord: [27.71, 85.32] },
  { name: "Austria", role: "Europe · partnership", coord: [48.21, 16.37] },
];

export const partners = {
  strategic: [
    { name: "Royal Scientific Society", short: "RSS", logo: "https://logo.clearbit.com/rss.gov.jo", site: "https://rss.gov.jo" },
    { name: "King Hussein Cancer Foundation", short: "KHCF", logo: "https://logo.clearbit.com/khcf.jo", site: "https://khcf.jo" },
  ],
  international: [
    { name: "Friedrich Naumann Foundation", short: "FNF", logo: "https://logo.clearbit.com/freiheit.org", site: "https://freiheit.org" },
    { name: "UNDP", short: "UNDP", logo: "https://logo.clearbit.com/undp.org", site: "https://undp.org" },
    { name: "Anna Lindh Foundation", short: "ALF", logo: "https://logo.clearbit.com/annalindhfoundation.org", site: "https://annalindhfoundation.org" },
    { name: "Amideast", short: "Amideast", logo: "https://logo.clearbit.com/amideast.org", site: "https://amideast.org" },
  ],
};

export const partnersMarquee = [
  "Royal Scientific Society",
  "King Hussein Cancer Foundation",
  "Friedrich Naumann Foundation",
  "UNDP",
  "Anna Lindh Foundation",
  "Amideast",
];

// Real success stories — DO NOT invent. Sourced from profile.
export const stories = [
  // Hub stories
  {
    slug: "aiesec-jordan",
    name: "AIESEC Jordan",
    arm: "hub",
    armColor: armColors.hub,
    summary:
      "The Jordanian branch grew within IYAF's infrastructure to become a regional actor in the AIESEC network, now registered independently as a non-profit organisation.",
    impact: "Operating independently as a recognised regional leader",
    image: L.storyHub,
    shape: "squircle",
  },
  {
    slug: "sabeen",
    name: "Sabeen",
    arm: "hub",
    armColor: armColors.hub,
    summary:
      "Artist Jude Naeem's cultural project, evolved into one of Jordan's most prominent youth-led art platforms. Annual events since 2017.",
    impact: "100+ artists empowered across 7 art forms",
    image: L.heroEvent,
    shape: "circle",
  },
  {
    slug: "tedx-teams",
    name: "TedX Teams",
    arm: "hub",
    armColor: armColors.hub,
    summary:
      "Five university TedX teams launched from the Hub: TedX Dabouq, Tabarbour, Ahlia University, Middle East University, and Applied Science University.",
    impact: "50 speakers platformed · 2,500 youth engaged",
    image: L.heroHeader,
    shape: "pill",
  },
  {
    slug: "tareeqak-akafak",
    name: "Tareeqak Akafak",
    arm: "hub",
    armColor: armColors.hub,
    summary:
      "A Jordanian-Austrian partnership that grew into a collaborative project implemented under IYAF and DammaWos. Now operating in four countries.",
    impact: "200+ individuals supported on path to employment",
    image: L.storyYouth,
    shape: "squircle",
  },
  {
    slug: "jnsa",
    name: "JNSA",
    arm: "hub",
    armColor: armColors.hub,
    summary:
      "Jordanian Nursing Student Association. IYAF facilitated the foundation for an International Nursing Student Association, with Jordan as Chapter 1.",
    impact: "Sustainable structure for nursing student development",
    image: L.about2,
    shape: "circle",
  },
  {
    slug: "mun-conferences",
    name: "MUN Conferences",
    arm: "hub",
    armColor: armColors.hub,
    summary:
      "Multiple university, school and independent Model UN clubs established with a common framework: identify problems, debate solutions, draft resolutions.",
    impact: "Hundreds of youth trained in research, diplomacy and advocacy",
    image: L.storyWorkshop,
    shape: "pill",
  },
  // Entrepreneurship stories
  {
    slug: "huggsies",
    name: "Huggsies",
    arm: "entrepreneurship",
    armColor: armColors.social,
    summary:
      "A social enterprise creating personalised stuffed animals from factory fabric waste. Children design their own toys while materials that would otherwise be discarded become something loved.",
    impact: "Turning factory waste into joy and sustainability lessons",
    image:
      "https://images.unsplash.com/photo-1610047860800-d35d5f9b3cf3?crop=entropy&cs=srgb&fm=jpg&w=1200",
    shape: "squircle",
  },
  {
    slug: "nur",
    name: "NUR",
    arm: "entrepreneurship",
    armColor: armColors.social,
    summary:
      "Custom-made and ready-to-wear traditional clothing tailored to Middle Eastern markets. Started in Jordan; now serving clients across Saudi Arabia, UAE, and Bahrain.",
    impact: "Regional reach across 4 markets",
    image:
      "https://images.unsplash.com/photo-1583744946564-b52ac1c389c8?crop=entropy&cs=srgb&fm=jpg&w=1200",
    shape: "pill",
  },
  {
    slug: "misto-chocolate",
    name: "Misto Chocolate",
    arm: "entrepreneurship",
    armColor: armColors.social,
    summary:
      "Premium artisanal chocolate crafted in Amman. Known for artistic presentation and high-quality 70% cocoa formulations, described as 'edible jewels'.",
    impact: "Elevating Jordanian artisanal chocolate regionally",
    image:
      "https://images.unsplash.com/photo-1606312619070-d48b4c652a52?crop=entropy&cs=srgb&fm=jpg&w=1200",
    shape: "circle",
  },
  // Employment stories
  {
    slug: "sarah-m",
    name: "Sarah M.",
    arm: "employment",
    armColor: armColors.blueLight,
    summary:
      "Recent graduate struggling to break into marketing. Through IYAF, refined her resume, learned LinkedIn strategies, practiced interviews with mentors.",
    impact: "Marketing Coordinator at a growing tech company (4 months in)",
    quote:
      "IYAF taught me how to present myself professionally and research companies effectively. The confidence I gained was as valuable as the skills.",
    image:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&w=1200",
    shape: "squircle",
  },
  {
    slug: "ahmed-k",
    name: "Ahmed K.",
    arm: "employment",
    armColor: armColors.blueLight,
    summary:
      "Underemployed in retail despite a finance degree. During the program, he built a financial analysis portfolio, refined technical skills, connected with finance-sector mentors.",
    impact: "Junior Financial Analyst at a regional bank (6 months in)",
    quote:
      "The program showed me that I had the technical skills. I just needed to learn how to communicate them and where to look.",
    image:
      "https://images.unsplash.com/photo-1633332755192-727a05c4013d?crop=entropy&cs=srgb&fm=jpg&w=1200",
    shape: "pill",
  },
];

export const values = [
  { tKey: "honesty" },
  { tKey: "action" },
  { tKey: "access" },
  { tKey: "cascade" },
  { tKey: "sustain" },
];

export const timeline = [
  { year: "2015", tKey: "y2015" },
  { year: "2016", tKey: "y2016" },
  { year: "2018", tKey: "y2018" },
  { year: "2020", tKey: "y2020" },
  { year: "2022", tKey: "y2022" },
  { year: "2024", tKey: "y2024" },
  { year: "2026", tKey: "y2026" },
  { year: "2027", tKey: "y2027" },
];

export const industries = [
  "Technology & IT",
  "Financial Services",
  "Healthcare",
  "Hospitality",
  "Manufacturing",
  "Retail",
  "Professional Services",
  "Education",
  "NGO / Development",
  "Government",
  "Creative Industries",
  "Logistics",
];

export const consultServices = [
  {
    name: "Innovation & Strategy",
    items: [
      "Innovation and design-thinking workshops",
      "Sustainability strategy development",
      "Youth engagement strategies",
      "Program design and theory of change",
    ],
  },
  {
    name: "Program Evaluation",
    items: [
      "Impact measurement frameworks",
      "Monitoring and evaluation systems",
      "Theory of change development",
      "Program effectiveness assessments",
    ],
  },
  {
    name: "Research & Analysis",
    items: [
      "Market research and feasibility studies",
      "Policy analysis and recommendations",
      "Evidence generation",
      "Stakeholder mapping",
    ],
  },
  {
    name: "Reporting Support",
    items: [
      "Third-party sustainability report review",
      "Report preparation assistance",
      "Stakeholder consultation facilitation",
      "Data collection and analysis support",
    ],
  },
];

export const scenarios = [
  {
    title: "The Entrepreneur's Journey",
    flow: ["Hub", "Social (Entrepreneurship)", "Venture", "Social (Employment)", "Consult Funding"],
    body:
      "A young entrepreneur uses the Hub for meeting space and joins the expert network. She enrols in an IYAF Social cohort as part of the 3% with genuine potential. After training, she connects with VCs through our database. Her startup graduates to IYAF Venture. Two years later, her company hires three IYAF Social employment graduates. Meanwhile, IYAF Consult revenue funded the scholarship that made her participation possible.",
  },
  {
    title: "Job Seeker + Corporate Sponsor",
    flow: ["Sponsor", "Social (Employment)", "Hub (Mentor)", "Sponsor Hire", "Hub Alumni", "Consult Reporting"],
    body:
      "A regional bank sponsors an IYAF Social employment cohort as part of their CSR. Ahmad completes the program: resume refined, interview skills sharpened. He's matched with a career mentor from the Hub expert network. He's hired by the sponsoring bank. He later returns to mentor the next cohort. The bank's sponsorship fee contributes to Consult capacity and is recognised in their annual sustainability report.",
  },
  {
    title: "The Corporate Sponsor's Return",
    flow: ["Sponsor", "Consult (Reporting)", "Social Cohorts", "Hub (Demo Day)", "Graduate becomes Supplier"],
    body:
      "A manufacturing company sponsors IYAF at the organisational level. In return: visibility, $15K+ saved in sustainability reporting fees, and talent-pipeline access. Their funds support IYAF Social cohorts. One graduate's packaging startup later becomes a supplier, discovered at a Hub demo day. The sponsor becomes a client, recruiter and supplier partner through one relationship.",
  },
  {
    title: "The Development Partner's Multiplier",
    flow: ["Foundation", "Social Design+Delivery", "Hub Training", "Consult Evaluation", "Foundation Renewal+Hires"],
    body:
      "An international foundation wants to support youth employment but lacks local implementation capacity. IYAF Social designs and delivers the program; Hub hosts training; Consult provides impact evaluation. The foundation demonstrates outcomes to their donors, renews for a second phase, and eventually hires two program graduates to lead initiatives in other countries.",
  },
  {
    title: "NGO Collaboration",
    flow: ["NGO", "Consult Co-Design", "Social Delivery", "Hub Experts", "Donor Expansion"],
    body:
      "A regional NGO focused on women's inclusion wants to add youth programming. IYAF Consult co-designs the approach; Social delivers the training; Hub connects participants with female founders in the expert network. The NGO's donor is impressed by results and funds expansion. A Social graduate's startup is hired to build the NGO's M&E system.",
  },
];

export const programGallery = [
  "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&w=1000",
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&w=1000",
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&w=1000",
  "https://images.pexels.com/photos/7651639/pexels-photo-7651639.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.unsplash.com/photo-1521574778337-d962ef81733d?crop=entropy&cs=srgb&fm=jpg&w=1000",
  "https://images.unsplash.com/photo-1594913517373-d1e63820a2d2?crop=entropy&cs=srgb&fm=jpg&w=1000",
  "https://images.unsplash.com/photo-1646579886135-068c73800308?crop=entropy&cs=srgb&fm=jpg&w=1000",
  "https://images.unsplash.com/photo-1629747490241-624f07d70e1e?crop=entropy&cs=srgb&fm=jpg&w=1000",
  "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?crop=entropy&cs=srgb&fm=jpg&w=1000",
  "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&w=1000",
  "https://images.pexels.com/photos/20123842/pexels-photo-20123842.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.unsplash.com/photo-1517256673644-36ad11246d21?crop=entropy&cs=srgb&fm=jpg&w=1000",
];

export const faq = [
  { q: "How can youth apply for your programs?", a: "Visit iyafglobal.com for program information and application processes, or email info@iyafglobal.com for guidance." },
  { q: "Are your programs free?", a: "Most youth programs are free or heavily subsidised through our social enterprise model. Our goal is accessibility, not profit." },
  { q: "What makes IYAF different from other youth organisations?", a: "Our 97/3 assessment model, dual focus on entrepreneurship AND employment, Hub infrastructure with legal support, and a self-sustaining social enterprise model. Together, a unique ecosystem." },
  { q: "How can my organisation partner with IYAF?", a: "Email info@iyafglobal.com to discuss strategic partnerships, sponsorships, consulting services, or talent pipeline partnerships." },
  { q: "Do you operate outside Jordan?", a: "Yes. We operate in 5 countries: Jordan, Lebanon, Egypt, Uganda and Nepal, reaching 13 communities." },
  { q: "How do you achieve 70% self-sustainability?", a: "Through IYAF Consult (professional services), Hub memberships, and strategic partnerships. 100% of consulting profit is reinvested in youth programs." },
  { q: "What is the 97/3 model?", a: "Research shows ~97% of startups fail. Our model gives honest assessment to all entrepreneurs, redirecting those unlikely to succeed toward better-suited paths, while intensively supporting the 3% with genuine potential." },
];
